import React from "react";

interface Props {
  /** Guidance revealed on hover. Also used as the icon's accessible label. */
  text: string;
  /**
   * Icon class. Defaults to the info circle; pass a question mark when the hint
   * explains why something is MISSING rather than offering guidance.
   */
  icon?: string;
  /**
   * Extra class on the BUBBLE, for a hint the default placement or width does
   * not suit. Two exist:
   *
   * - `field-hint__bubble--below` opens the bubble downwards — for an icon near
   *   the top of its container, such as the first row of a table, where the
   *   default upward bubble covers the column headers.
   * - `field-hint__bubble--fit` shrinks it to its content — for a one-line hint,
   *   which the default paragraph-width bubble leaves mostly empty.
   */
  bubbleClass?: string;
}

/**
 * Info icon that reveals `text` on hover or keyboard focus, instead of parking
 * long guidance permanently under a field. Mirrors the hint in the TCP admin
 * dashboard (app/views/tcp_admin/shared/_field_hint.html.erb in tcp-api).
 *
 * Styling: src/v2/components/shared/_field-hint.scss
 *
 * Pair it with a label inside a flex row, and give the label `mb-0` so the icon
 * lines up with the label text rather than its bottom margin:
 *
 *   <div className="d-flex align-items-center mb-1">
 *     <label htmlFor="thing" className="mb-0">Thing</label>
 *     <FieldHint text="…" />
 *   </div>
 */
const FieldHint: React.FC<Props> = ({ text, icon = "fas fa-info-circle", bubbleClass = "" }) => (
  <span className="field-hint">
    <i className={`${icon} field-hint__icon`} tabIndex={0} role="img" aria-label={text} />
    <span className={`field-hint__bubble ${bubbleClass}`.trim()} role="tooltip">
      {text}
    </span>
  </span>
);

export default FieldHint;
